import React, { useEffect, useState } from 'react';
import { clinicService } from '../services/clinicService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

function ClinicDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadStats();
    loadAppointments();
  }, [filterDate]);

  const loadStats = async () => {
    try {
      const data = await clinicService.getStats();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const params = { 
        date: filterDate,
        per_page: 50 
      };
      const response = await clinicService.getAppointments(params);
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Failed to load appointments', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      booked: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleViewAppointment = (appointment) => {
    navigate('/clinic/appointments', { state: { viewAppointment: appointment } });
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  const statCards = [
    { title: 'Total Doctors', value: stats?.total_doctors || 0, icon: '👨‍⚕️', color: 'from-blue-500 to-blue-600' },
    { title: 'Total Patients', value: stats?.total_patients || 0, icon: '👥', color: 'from-green-500 to-green-600' },
    { title: 'Total Staff', value: stats?.total_staff || 0, icon: '👔', color: 'from-purple-500 to-purple-600' },
    { title: 'Today\'s Appointments', value: stats?.todays_appointments || 0, icon: '📅', color: 'from-orange-500 to-orange-600' },
    { title: 'Pending Appointments', value: stats?.pending_appointments || 0, icon: '⏳', color: 'from-yellow-500 to-yellow-600' },
    { title: 'Completed Today', value: stats?.completed_appointments || 0, icon: '✅', color: 'from-teal-500 to-teal-600' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Welcome to Your Clinic Dashboard</h1>
        <p className="text-gray-600 mt-2">Overview of your clinic's operations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br ${card.color} text-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-opacity-80 text-sm font-medium">{card.title}</p>
                <p className="text-4xl font-bold mt-2">{card.value}</p>
              </div>
              <div className="text-6xl opacity-80">{card.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/clinic/appointments', { state: { openBookModal: true } })}
              className="w-full text-left p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">📅</span>
                <div>
                  <p className="font-semibold text-blue-900">Book Appointment</p>
                  <p className="text-sm text-blue-700">Schedule a new patient appointment</p>
                </div>
              </div>
            </button>
            <a href="/clinic/patients" className="block p-4 bg-green-50 hover:bg-green-100 rounded-lg transition">
              <div className="flex items-center gap-3">
                <span className="text-2xl">👥</span>
                <div>
                  <p className="font-semibold text-green-900">Register Patient</p>
                  <p className="text-sm text-green-700">Add a new patient to the system</p>
                </div>
              </div>
            </a>
            <a href="/clinic/doctors" className="block p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition">
              <div className="flex items-center gap-3">
                <span className="text-2xl">👨‍⚕️</span>
                <div>
                  <p className="font-semibold text-purple-900">Manage Doctors</p>
                  <p className="text-sm text-purple-700">View and manage doctor profiles</p>
                </div>
              </div>
            </a>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">📋 Recent Activity</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold">📅</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">Appointments</p>
                <p className="text-xs text-gray-600">{stats?.todays_appointments || 0} scheduled for today</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">👥</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">Patients</p>
                <p className="text-xs text-gray-600">{stats?.total_patients || 0} patients registered</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">👨‍⚕️</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">Doctors</p>
                <p className="text-xs text-gray-600">{stats?.total_doctors || 0} doctors available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Appointments Section */}
      <div className="mt-8 bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">📅 Upcoming Appointments</h3>
          <div className="flex gap-3 items-center">
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => setFilterDate(new Date().toISOString().split('T')[0])}
              className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium"
            >
              Today
            </button>
          </div>
        </div>

        <div className="mb-4 text-sm text-gray-600">
          Showing appointments for: <span className="font-semibold text-gray-900">{formatDate(filterDate)}</span>
        </div>

        {appointments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">No appointments scheduled for this date</p>
            <button
              onClick={() => navigate('/clinic/appointments', { state: { openBookModal: true } })}
              className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold"
            >
              ➕ Book New Appointment
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {appointments
              .sort((a, b) => new Date(a.slot_start) - new Date(b.slot_start))
              .map((appointment) => (
              <div
                key={appointment.id}
                onClick={() => handleViewAppointment(appointment)}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="bg-blue-100 rounded-lg px-4 py-2 text-center min-w-[100px]">
                    <p className="text-sm font-semibold text-blue-900">
                      {formatTime(appointment.slot_start)}
                    </p>
                    <p className="text-xs text-blue-600">
                      {formatTime(appointment.slot_end)}
                    </p>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900">{appointment.patient?.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadge(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Dr. {appointment.doctor?.name}</span>
                      {appointment.doctor?.specialization && (
                        <span className="text-gray-400"> • {appointment.doctor.specialization}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {appointment.appointment_number} • {appointment.patient?.mobile}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewAppointment(appointment);
                    }}
                    className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium text-sm"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClinicDashboard;
